'use client'

import { useRouter } from 'next/navigation'
import { useLocale } from 'next-intl'
import { createContext, useCallback, useContext } from 'react'
import { IGoogleAddressOption } from '@/features/google-places/google-places-restaurants-search'
import { Locale } from '@/setup/locales'
import { getPathname } from '@/setup/routing'
import { IChildrenProps } from '@/typings/children-props'
import { noOp } from '@/utils/functions/noOp'

interface IBecomeAPartnerContext {
    onSelect: (option: IGoogleAddressOption | null) => void
}

const BecomeAPartnerContext = createContext<IBecomeAPartnerContext>({
    onSelect: noOp,
})

export const BecomeAPartnerProvider = ({ children }: IChildrenProps) => {
    const locale = useLocale()

    const router = useRouter()

    const onSelect = useCallback(
        async (option: IGoogleAddressOption | null) => {
            const placeId = option?.value.placeId

            if (!placeId) return

            const placeIdSlug = slugify(option?.value.name ?? '') + '--' + placeId

            router.push(
                getPathname('/become-a-partner/[placeIdSlug]', { placeIdSlug }, locale as Locale)
            )
        },
        [locale, router]
    )

    return (
        <BecomeAPartnerContext.Provider value={{ onSelect }}>
            {children}
        </BecomeAPartnerContext.Provider>
    )
}

export const useBecomeAPartnerContext = () => useContext(BecomeAPartnerContext)

function slugify(input: string): string {
    // Normalize the string to NFD Unicode normal form, decomposing special characters
    const normalized = input.normalize('NFD').replace(/[\u0300-\u036f]/g, '')

    // Replace non-alphanumeric characters with hyphens
    const slug = normalized
        .replace(/\s+/g, '-') // Replace spaces with hyphens
        .replace(/[^a-zA-Z0-9\-]/g, '') // Remove all non-word chars
        .replace(/\-\-+/g, '-') // Replace multiple hyphens with a single hyphen
        .toLowerCase() // Convert to lowercase

    return slug
}
